import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Zap, Brain, ArrowRight, Shield, Sparkles, Mail, Lock, User, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Logo from '../components/Logo';
import { trackAccess } from '../utils/accessTracking';
import { AUTH_CONFIG } from '../config/auth';
import { debugEmailConfig } from '../utils/emailDebug';
import '../utils/testEnv'; // Load test utility

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated, setUser } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Debug email configuration on component mount (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugEmailConfig();
    }
  }, []);

  const validateForm = (isDemo: boolean = false) => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Only validate password for full sign-in, not demo
    if (!isDemo) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password !== AUTH_CONFIG.PASSWORD) {
        newErrors.password = 'Incorrect password';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(false)) {
      return;
    }
    
    setIsLoading(true);
    
    // Simulate authentication
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const userEmail = formData.email.trim();
    const userName = formData.name.trim() || 'User';
    
    // Track access (this will also send email notification)
    await trackAccess(userEmail, userName, 'full');
    
    setUser({ name: userName, email: userEmail });
    setIsAuthenticated(true);
    setIsLoading(false);
    navigate('/dashboard');
  };

  const handleDemoMode = async () => {
    // Validate email for demo mode
    if (!validateForm(true)) {
      return;
    }
    
    setIsLoading(true);
    
    // Simulate brief loading
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const userEmail = formData.email.trim();
    const userName = formData.name.trim() || 'Demo User';
    
    // Track access (this will also send email notification)
    await trackAccess(userEmail, userName, 'demo');
    
    setUser({ name: userName, email: userEmail });
    setIsAuthenticated(true);
    setIsLoading(false);
    navigate('/dashboard');
  };

  return (
    <div style={styles.container}>
      {/* Animated Background */}
      <div style={styles.backgroundPattern}>
        <motion.div
          style={styles.floatingOrb1}
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          style={styles.floatingOrb2}
          animate={{
            y: [0, 40, 0],
            x: [0, -30, 0],
            scale: [1, 0.9, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          style={styles.floatingOrb3}
          animate={{
            y: [0, 20, 0],
            x: [0, 15, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div style={styles.content}>
        {/* Left Side - Branding */}
        <motion.div
          style={styles.brandingSide}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            style={styles.logo}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
          >
            <Logo variant="white" size="lg" />
          </motion.div>

          <motion.h1
            style={styles.headline}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Eradicating employee disengagement,
            <br />
            <span style={styles.headlineAccent}>Autonomously.</span>
          </motion.h1>

          <motion.p
            style={styles.subheadline}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            Dynami detects early signs of disengagement and takes autonomous action to re-engage employees, support managers, and protect your talent.
          </motion.p>

          <motion.div
            style={styles.features}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <Brain size={20} color="#FF68C5" />
              </div>
              <span>AI-Powered Predictions</span>
            </div>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <Zap size={20} color="#FFC800" />
              </div>
              <span>Autonomous Actions</span>
            </div>
            <div style={styles.feature}>
              <div style={styles.featureIcon}>
                <Shield size={20} color="#06BCCA" />
              </div>
              <span>Human-First Approach</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Right Side - Form */}
        <motion.div
          style={styles.formSide}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>Welcome to Dynami</h2>
            <p style={styles.formSubtitle}>Sign in to access your engagement dashboard</p>

            <form onSubmit={handleSignIn} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <User size={16} style={{ marginRight: '6px', opacity: 0.6 }} />
                  Full Name (Optional)
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your name"
                    style={{
                      ...styles.input,
                      ...(focusedField === 'name' ? styles.inputFocused : {}),
                    }}
                  />
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <Mail size={16} style={{ marginRight: '6px', opacity: 0.6 }} />
                  Email Address
                </label>
                <div style={styles.inputWrapper}>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="you@company.com"
                    style={{
                      ...styles.input,
                      ...(focusedField === 'email' ? styles.inputFocused : {}),
                      ...(errors.email ? styles.inputError : {}),
                    }}
                  />
                </div>
                {errors.email && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={styles.errorMessage}
                  >
                    <AlertCircle size={14} />
                    {errors.email}
                  </motion.div>
                )}
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <Lock size={16} style={{ marginRight: '6px', opacity: 0.6 }} />
                  Password
                </label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Enter your password"
                    style={{
                      ...styles.input,
                      ...(focusedField === 'password' ? styles.inputFocused : {}),
                      ...(errors.password ? styles.inputError : {}),
                    }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.passwordToggle}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={styles.errorMessage}
                  >
                    <AlertCircle size={14} />
                    {errors.password}
                  </motion.div>
                )}
              </div>

              <div style={styles.forgotPassword}>
                <button
                  type="button"
                  className="forgot-password-link"
                  style={styles.forgotPasswordLink}
                  onClick={() => {/* Handle forgot password */}}
                >
                  Forgot password?
                </button>
              </div>

              <motion.button
                type="submit"
                style={styles.signInButton}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div style={styles.loader} />
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={18} />
                  </>
                )}
              </motion.button>
            </form>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span>or</span>
              <div style={styles.dividerLine} />
            </div>

            <motion.button
              type="button"
              className="demo-button"
              onClick={handleDemoMode}
              style={styles.demoButton}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <div style={styles.loader} />
              ) : (
                <>
                  <Sparkles size={18} />
                  Enter Demo Mode
                </>
              )}
            </motion.button>

            <p style={styles.demoNote}>
              Demo mode requires email only. Explore with sample data.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #431652 0%, #69277F 50%, #1a0a24 100%)',
    position: 'relative',
    overflow: 'hidden',
    padding: '20px',
  },
  backgroundPattern: {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  floatingOrb1: {
    position: 'absolute',
    top: '10%',
    left: '10%',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(6, 188, 202, 0.3) 0%, transparent 70%)',
    filter: 'blur(40px)',
  },
  floatingOrb2: {
    position: 'absolute',
    bottom: '10%',
    right: '10%',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255, 104, 197, 0.25) 0%, transparent 70%)',
    filter: 'blur(50px)',
  },
  floatingOrb3: {
    position: 'absolute',
    top: '50%',
    right: '30%',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255, 200, 0, 0.2) 0%, transparent 70%)',
    filter: 'blur(30px)',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: '80px',
    maxWidth: '1200px',
    width: '100%',
    zIndex: 1,
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
  },
  brandingSide: {
    flex: 1,
    minWidth: '300px',
    maxWidth: '500px',
    color: 'white',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '40px',
  },
  logoText: {
    fontSize: '32px',
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
    letterSpacing: '-1px',
  },
  headline: {
    fontSize: '48px',
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: '24px',
    fontFamily: 'var(--font-heading)',
    color: 'white',
  },
  headlineAccent: {
    background: 'linear-gradient(90deg, #06BCCA 0%, #FF68C5 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  subheadline: {
    fontSize: '18px',
    lineHeight: 1.6,
    opacity: 0.9,
    marginBottom: '40px',
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '16px',
    opacity: 0.9,
  },
  featureIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  formSide: {
    flex: 1,
    minWidth: '300px',
    maxWidth: '440px',
  },
  formCard: {
    background: 'white',
    borderRadius: '24px',
    padding: '48px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  formTitle: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '8px',
    color: '#431652',
  },
  formSubtitle: {
    fontSize: '15px',
    color: '#6B7280',
    marginBottom: '32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  inputWrapper: {
    position: 'relative',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    paddingRight: '44px',
    fontSize: '15px',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.2s ease',
    background: '#F9FAFB',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  },
  inputFocused: {
    borderColor: '#69277F',
    background: 'white',
    boxShadow: '0 0 0 4px rgba(105, 39, 127, 0.1)',
  },
  inputError: {
    borderColor: '#EF4444',
    background: '#FEF2F2',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#EF4444',
    marginTop: '6px',
  },
  forgotPassword: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '-8px',
  },
  forgotPasswordLink: {
    background: 'none',
    border: 'none',
    color: '#69277F',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '4px 0',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  passwordWrapper: {
    position: 'relative',
  },
  passwordToggle: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '4px',
    background: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s ease',
  },
  signInButton: {
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: 600,
    color: 'white',
    background: 'linear-gradient(135deg, #69277F 0%, #431652 100%)',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '8px',
    border: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 12px rgba(105, 39, 127, 0.2)',
  },
  loader: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '24px 0',
    color: '#9CA3AF',
    fontSize: '14px',
    position: 'relative',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#E5E7EB',
  },
  demoButton: {
    width: '100%',
    padding: '16px',
    fontSize: '16px',
    fontWeight: 600,
    color: '#69277F',
    background: 'linear-gradient(135deg, rgba(6, 188, 202, 0.15) 0%, rgba(255, 104, 197, 0.15) 100%)',
    borderRadius: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: '2px solid rgba(105, 39, 127, 0.2)',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(105, 39, 127, 0.1)',
  },
  demoNote: {
    fontSize: '13px',
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: '12px',
  },
};

// Add keyframe animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  input:focus {
    border-color: #69277F !important;
    background: white !important;
    box-shadow: 0 0 0 4px rgba(105, 39, 127, 0.1) !important;
  }
  
  button[type="submit"]:hover:not(:disabled) {
    box-shadow: 0 8px 24px rgba(105, 39, 127, 0.3) !important;
    transform: translateY(-1px);
  }
  
  button[type="submit"]:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  button[type="button"].demo-button:hover {
    box-shadow: 0 4px 12px rgba(105, 39, 127, 0.2) !important;
    border-color: rgba(105, 39, 127, 0.4) !important;
  }
  
  button.forgot-password-link:hover {
    color: #431652 !important;
    text-decoration: underline;
  }
  
  button.password-toggle:hover {
    color: #69277F !important;
  }
`;
document.head.appendChild(styleSheet);

export default SignIn;

